/**
 * Role Types Tab (Epic 1 — Roltypes & niveau-bepaling v1, Story 1.1)
 *
 * Manage roltypes (functieloonhuis catalogus): naam, omschrijving, default
 * niveau. Roltypes kunnen niet verwijderd worden, uitsluitend gedeactiveerd/
 * gereactiveerd (v1-PRD open vraag 4) — er is dus geen "Verwijderen"-knop.
 *
 * Kleuren volgen het XXtract-palet (navy/teal/groen/rood), NIET de
 * Tailwind-blauw/rood-classes van FunctionsTab.tsx (zie Task 6, NFR-1).
 */

import { useState } from 'react';
import {
  useRoleTypes,
  useCreateRoleType,
  useUpdateRoleType,
  useDeactivateRoleType,
  useReactivateRoleType,
  useNiveaus,
} from '../useSettings';
import type { RoleTypeDTO, CreateRoleTypeRequest, UpdateRoleTypeRequest } from '@functieloonhuis/shared';

interface RoleTypeFormData {
  name: string;
  description: string;
  defaultNiveauId: number | null;
}

const emptyFormData: RoleTypeFormData = {
  name: '',
  description: '',
  defaultNiveauId: null,
};

export function RoleTypesTab() {
  const { data: roleTypes, isLoading, error } = useRoleTypes(true);
  const { data: niveaus } = useNiveaus();
  const createRoleType = useCreateRoleType();
  const updateRoleType = useUpdateRoleType();
  const deactivateRoleType = useDeactivateRoleType();
  const reactivateRoleType = useReactivateRoleType();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingRoleType, setEditingRoleType] = useState<RoleTypeDTO | null>(null);
  const [formData, setFormData] = useState<RoleTypeFormData>(emptyFormData);
  const [formError, setFormError] = useState<string | null>(null);

  // Alleen "geconfigureerde" niveaus (minPunten !== null) mogen als default
  // niveau gekozen worden — niveau 5/6 zijn ongeconfigureerd (PRD §8.4,
  // AD-11). Consistent met backend niveauService.getConfiguredNiveaus.
  const configuredNiveaus = (niveaus ?? []).filter((n) => n.minPunten !== null);

  const niveauName = (id: number): string => {
    return niveaus?.find((n) => n.id === id)?.name ?? `Niveau ${id}`;
  };

  const resetForm = () => {
    setFormData(emptyFormData);
    setEditingRoleType(null);
    setFormError(null);
    setIsFormOpen(false);
  };

  const handleAdd = () => {
    resetForm();
    setIsFormOpen(true);
  };

  const handleEdit = (roleType: RoleTypeDTO) => {
    setEditingRoleType(roleType);
    setFormData({
      name: roleType.name,
      description: roleType.description ?? '',
      defaultNiveauId: roleType.defaultNiveauId,
    });
    setFormError(null);
    setIsFormOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!formData.defaultNiveauId) {
      setFormError('Kies een default niveau.');
      return;
    }

    try {
      if (editingRoleType) {
        const updateData: UpdateRoleTypeRequest = {
          name: formData.name,
          // Empty text means "clear the description" on update — send `null`
          // explicitly, not `undefined` (which the backend treats as "leave
          // unchanged" and would silently keep the old description).
          description: formData.description.trim() === '' ? null : formData.description,
          defaultNiveauId: formData.defaultNiveauId,
        };
        await updateRoleType.mutateAsync({ id: editingRoleType.id, data: updateData });
      } else {
        const createData: CreateRoleTypeRequest = {
          name: formData.name,
          description: formData.description || undefined,
          defaultNiveauId: formData.defaultNiveauId,
        };
        await createRoleType.mutateAsync(createData);
      }
      resetForm();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Er is een fout opgetreden bij het opslaan.';
      setFormError(message);
    }
  };

  const handleToggleActive = async (roleType: RoleTypeDTO) => {
    if (roleType.isActive) {
      await deactivateRoleType.mutateAsync(roleType.id);
    } else {
      await reactivateRoleType.mutateAsync(roleType.id);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2F5A7A]"></div>
        <span className="ml-3 text-gray-600">Laden...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-[#D64545]/30 rounded-lg p-4">
        <p className="text-sm text-[#D64545]">Fout bij het laden van roltypes.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Roltypes</h2>
          <p className="text-sm text-gray-500">
            Beheer het functieloonhuis: roltypes en hun default niveau
          </p>
        </div>
        <button
          data-testid="add-role-type-button"
          onClick={handleAdd}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#2F5A7A] hover:bg-[#254a63]"
        >
          <svg className="mr-2 -ml-1 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Nieuw Roltype
        </button>
      </div>

      {/* Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500/75 transition-opacity" onClick={resetForm}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="relative z-10 inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <form onSubmit={handleSubmit}>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {editingRoleType ? 'Roltype Bewerken' : 'Nieuw Roltype'}
                </h3>

                {formError && (
                  <div
                    data-testid="role-type-form-error"
                    className="mb-4 rounded-md bg-red-50 border border-[#D64545]/30 px-3 py-2 text-sm text-[#D64545]"
                  >
                    {formError}
                  </div>
                )}

                <div className="space-y-4">
                  {/* Name */}
                  <div>
                    <label htmlFor="role-type-name" className="block text-sm font-medium text-gray-700">
                      Naam
                    </label>
                    <input
                      type="text"
                      id="role-type-name"
                      data-testid="role-type-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      maxLength={100}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#54949E] focus:ring-[#54949E] sm:text-sm"
                      required
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label htmlFor="role-type-description" className="block text-sm font-medium text-gray-700">
                      Omschrijving
                    </label>
                    <textarea
                      id="role-type-description"
                      data-testid="role-type-description-input"
                      rows={2}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      maxLength={500}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#54949E] focus:ring-[#54949E] sm:text-sm"
                    />
                  </div>

                  {/* Default Niveau */}
                  <div>
                    <label htmlFor="role-type-default-niveau" className="block text-sm font-medium text-gray-700">
                      Default niveau
                    </label>
                    <select
                      id="role-type-default-niveau"
                      data-testid="role-type-default-niveau-select"
                      value={formData.defaultNiveauId ?? ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          defaultNiveauId: e.target.value ? Number(e.target.value) : null,
                        })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#54949E] focus:ring-[#54949E] sm:text-sm"
                      required
                    >
                      <option value="">Kies een niveau</option>
                      {configuredNiveaus.map((niveau) => (
                        <option key={niveau.id} value={niveau.id}>
                          {niveau.name}
                        </option>
                      ))}
                      {/* Bij het bewerken van een roltype waarvan het huidige default
                          niveau niet (meer) "geconfigureerd" is, toon 'm alsnog als
                          disabled optie — anders rendert de select een misleidende
                          lege placeholder terwijl formData.defaultNiveauId wel gezet is. */}
                      {formData.defaultNiveauId !== null &&
                        !configuredNiveaus.some((n) => n.id === formData.defaultNiveauId) && (
                          <option value={formData.defaultNiveauId} disabled>
                            {niveauName(formData.defaultNiveauId)} (niet beschikbaar)
                          </option>
                        )}
                    </select>
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Annuleren
                  </button>
                  <button
                    type="submit"
                    data-testid="role-type-submit-button"
                    disabled={createRoleType.isPending || updateRoleType.isPending}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#2F5A7A] hover:bg-[#254a63] disabled:opacity-50"
                  >
                    {createRoleType.isPending || updateRoleType.isPending ? 'Opslaan...' : 'Opslaan'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Role Types List */}
      {roleTypes && roleTypes.length > 0 ? (
        <div className="overflow-hidden border border-gray-200 rounded-lg">
          <table data-testid="role-types-table" className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Roltype
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Default niveau
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acties
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {roleTypes.map((roleType) => (
                <tr key={roleType.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{roleType.name}</div>
                      {roleType.description && (
                        <div className="text-sm text-gray-500 truncate max-w-xs">{roleType.description}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {roleType.defaultNiveauName ?? niveauName(roleType.defaultNiveauId)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      data-testid="role-type-row-toggle-active"
                      onClick={() => handleToggleActive(roleType)}
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer ${
                        roleType.isActive ? 'bg-[#B7D945]/30 text-[#3f5c0d]' : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      <span data-testid="role-type-row-status">
                        {roleType.isActive ? 'Actief' : 'Inactief'}
                      </span>
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEdit(roleType)}
                      className="text-[#54949E] hover:text-[#2F5A7A]"
                    >
                      Bewerken
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">Geen roltypes gevonden</h3>
          <p className="mt-2 text-sm text-gray-500">Begin met het toevoegen van een roltype.</p>
        </div>
      )}
    </div>
  );
}
